import React from 'react';
import './style.css';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const OneLineChart = ({
    title,
    data,
    labels,
    chartTitle,
    roundTo = 5000,
    negativeMinAllowed = false,
    deviceType = 'desktop',
    chartType = 'default'
}) => {
    const chartData = {
        labels,
        datasets: [
            {
                label: title,
                data: data,
                borderColor: '#000000',         // Line color
                backgroundColor: '#F6E6DB',      // Point fill color
                pointBorderColor: '#D8A270',    // Point border color
                pointRadius: 4,                 // Point size
                pointHoverRadius: 6,            // Hover point size
                pointBackgroundColor: '#F6E6DB', // Point fill color
                pointHoverBackgroundColor: '#F6E6DB', // Hover point fill color
                pointHoverBorderColor: '#D8A270',    // Hover point border color
                pointHoverBorderWidth: 2,           // Hover point border width
                tension: 0.1,
                datalabels: {
                    display: false
                }
            }
        ]
    };

    // Function to calculate axis bounds
    const calculateYAxisBounds = () => {
        const allValues = [...data];
        
        // Get min and max values
        const minValue = Math.min(...allValues);
        const maxValue = Math.max(...allValues);
        
        // Add 10% padding to the range
        const range = maxValue - minValue;
        const padding = range * 0.1;
        
        // Round to nearest roundTo value for cleaner numbers
        const min = negativeMinAllowed 
            ? Math.floor((minValue - padding) / roundTo) * roundTo
            : Math.max(0, Math.floor((minValue - padding) / roundTo) * roundTo);
        const max = Math.ceil((maxValue + padding) / roundTo) * roundTo;
        
        // Calculate appropriate step size based on range
        const totalRange = max - min;
        const stepSize = Math.ceil(totalRange / 6 / roundTo) * roundTo; // Aim for ~6 steps

        return { min, max, stepSize };
    };

    const { min, max, stepSize } = calculateYAxisBounds();

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            ...(chartTitle && {
                title: {
                    display: true,
                    text: chartTitle,
                    font: {
                        size: 12,
                        weight: 'bold'
                    },
                    padding: {
                        bottom: 30
                    }
                }
            }),
            legend: {
                display: false  // Hide the legend
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        if (context.parsed.y !== null) {
                            return new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                            }).format(context.parsed.y);
                        }
                        return '';
                    }
                }
            },
            datalabels: {
                display: false  // This will ensure no labels appear on the points
            }
        },
        scales: {
            y: {
                beginAtZero: false,
                min: min,
                max: max,
                ticks: {
                    stepSize: stepSize,
                    callback: function (value) {
                        return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                            notation: 'compact',
                            compactDisplay: 'short'
                        }).format(value);
                    }
                },
                grid: {
                    drawBorder: true,
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    return (
        <Line data={chartData} options={options} />
    );
};

export default OneLineChart;

